/**
 * PodBeneficiaryDialog — account-scoped POD beneficiary management for
 * DDA/SAV/CD accounts. Fires from the Products tab row action on the CIF
 * Detail page.
 *
 * Uses the shared BeneficiaryPicker primitive for CRUD of primary/contingent
 * entries, then dispatches through usePodBeneficiary for the CustRelAdd
 * cascade.
 *
 * Scope limitation: the picker today does not emit cifId for freshly-entered
 * beneficiaries (those would need a new CustAdd first). For the Monday demo
 * we require each entry to select an existing CIF — the "new beneficiary"
 * path will light up when the picker's CIF-search integration lands
 * (memory/benef-architecture-v3.md Wave 4).
 */

import { useMemo, useState } from 'react';
import { Banner, Button, ModalShell, BeneficiaryPicker } from '@/components/primitives';
import type { BeneficiaryEntry } from '@/components/primitives';
import { usePodBeneficiary, type PodChangeEntry } from './usePodBeneficiary';
import type { AccountScopedMaintProps } from '../types';

export interface PodBeneficiaryDialogProps extends AccountScopedMaintProps {
  /** Pre-existing beneficiaries (loaded by the host from CustInq → PersonArray). */
  initialBeneficiaries?: ReadonlyArray<BeneficiaryEntry>;
}

export function PodBeneficiaryDialog(props: PodBeneficiaryDialogProps): JSX.Element | null {
  const {
    open, onClose, onSaved, cifId, acctId, acctType, prodDesc,
    officerCode = '000', readOnly = false, initialBeneficiaries = [],
  } = props;

  const { run } = usePodBeneficiary();
  const [entries, setEntries] = useState<BeneficiaryEntry[]>(() =>
    initialBeneficiaries.slice(),
  );
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  // Narrow acctType for BeneficiaryPicker dispatch.
  const pickerAcctType = useMemo((): '10' | '20' | '30' | '50' | '60' => {
    if (acctType === '10' || acctType === '20' || acctType === '30' ||
        acctType === '50' || acctType === '60') {
      return acctType;
    }
    return '10';
  }, [acctType]);

  const primarySum = entries
    .filter((e) => e.role === 'primary')
    .reduce((sum, e) => sum + (e.percentage || 0), 0);
  const contingentSum = entries
    .filter((e) => e.role === 'contingent')
    .reduce((sum, e) => sum + (e.percentage || 0), 0);

  const canSave = useMemo((): boolean => {
    if (readOnly) return false;
    if (submitting) return false;
    if (entries.length === 0) return false;
    // Enforce percent rules before submit.
    if (primarySum !== 100) return false;
    if (contingentSum !== 0 && contingentSum !== 100) return false;
    // Every entry must have firstName + lastName.
    if (entries.some((e) => !e.firstName.trim() || !e.lastName.trim())) return false;
    return true;
  }, [readOnly, submitting, entries, primarySum, contingentSum]);

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSubmitting(true);
    setErr(null);

    // Build change entries. For the demo path we treat every row as an Add —
    // a full diff (match against initialBeneficiaries to emit removes for
    // missing ids) is a Wave-4 enhancement.
    const changes: PodChangeEntry[] = [];
    for (const e of entries) {
      if (!e.cifId) {
        // Skip rows that aren't linked to a CIF yet — the picker's new-CIF
        // path isn't wired. Surface as a soft error.
        continue;
      }
      changes.push({
        op: 'add',
        role: e.role === 'primary' ? 'primary' : 'contingent',
        beneficiaryCifId: e.cifId,
        percentage: e.percentage,
      });
    }

    if (changes.length === 0) {
      setErr('Each beneficiary must be linked to an existing CIF. Add-new-beneficiary is Wave 4.');
      setSubmitting(false);
      return;
    }

    const result = await run({
      cifId,
      acctId,
      acctType,
      officerCode,
      entries: changes,
    });
    setSubmitting(false);
    if (!result.ok) {
      setErr(result.error ?? 'Unknown error');
      return;
    }
    onSaved?.();
    onClose();
  };

  if (!open) return null;

  return (
    <ModalShell
      open={open}
      onClose={submitting ? () => { /* block */ } : onClose}
      title={`Manage Beneficiaries — ${acctId}${prodDesc ? ' (' + prodDesc + ')' : ''}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          {!readOnly && (
            <Button variant="primary" onClick={() => { void handleSave(); }} disabled={!canSave}>
              {submitting ? 'Saving…' : 'Save Beneficiaries'}
            </Button>
          )}
        </>
      }
    >
      {readOnly && (
        <Banner tone="warn" title="Read-only">
          You do not have the Beneficiary-Maintenance grant.
        </Banner>
      )}

      {err && <Banner tone="err" title="Save failed">{err}</Banner>}

      <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
        CIF <code style={{ fontFamily: 'var(--font-mono)' }}>{cifId}</code>
        {' · '}Account <code style={{ fontFamily: 'var(--font-mono)' }}>{acctId}</code>
      </div>

      <BeneficiaryPicker
        accountType={pickerAcctType}
        ownerCifId={cifId}
        initialBeneficiaries={initialBeneficiaries}
        onChange={(next) => { setEntries(next); setErr(null); }}
        disabled={readOnly || submitting}
      />

      <div style={{ display: 'flex', gap: 'var(--sp-5)', fontSize: 12, color: 'var(--ink-600)' }}>
        <div>Primary sum: <strong style={{ color: primarySum === 100 ? 'var(--pcb-green)' : 'var(--pcb-red)' }}>{primarySum}%</strong></div>
        <div>Contingent sum: <strong style={{ color: (contingentSum === 0 || contingentSum === 100) ? 'var(--pcb-green)' : 'var(--pcb-red)' }}>{contingentSum}%</strong></div>
      </div>

      {/* TODO: wire disclosure packet when Team 2's engine lands
          (Beneficiary Designation form + updated signature card). */}
    </ModalShell>
  );
}
